// -----------------------------------------------------------------------------
// ui/timeline/timeline.ts — the CANVAS timeline view.
//
// Draws the ruler, track lanes, clips and playhead onto a 2D canvas (the timeline
// is canvas-rendered, not DOM — CLAUDE.md §4). It also owns the geometry math
// (time↔x, hit-testing) that interactions.ts uses, and a transient `drag` state
// it renders while a clip is being moved/trimmed before the edit is committed.
//
// It only READS the store + player; all edits go through commands dispatched by
// interactions.ts.
// -----------------------------------------------------------------------------
import { type Clip, type ID, projectDuration } from '../../core/model';
import { formatTimecode } from '../../shared/time';
import type { EditorContext } from '../context';

export const HEADER_W = 124;
export const RULER_H = 26;
export const TRACK_H = 58;
const CLIP_PAD = 6;
export const EDGE_PX = 7;

export interface DragState {
  clipId: ID;
  mode: 'move' | 'trim-start' | 'trim-end';
  startSec: number;
  durationSec: number;
  inSec: number;
  trackIndex: number;
}

export type HitResult =
  | { kind: 'ruler' }
  | { kind: 'remove-track'; trackIndex: number }
  | { kind: 'clip'; clipId: ID; trackIndex: number; edge: 'start' | 'end' | 'body' }
  | { kind: 'empty'; trackIndex: number }
  | { kind: 'outside' };

export class TimelineView {
  readonly canvas: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;

  constructor(private readonly ctx: EditorContext) {
    this.canvas = document.createElement('canvas');
    const g = this.canvas.getContext('2d');
    if (!g) throw new Error('2D canvas context unavailable for the timeline.');
    this.g = g;
  }

  get pxPerSecond(): number {
    return this.ctx.signals.pxPerSecond.get();
  }

  drag: DragState | null = null;
  // Time of the current snap (set while a drag snaps to a target), drawn as a
  // dashed guideline so snapping is visible. null when not snapped.
  snapLine: number | null = null;

  // ---- geometry --------------------------------------------------------------
  timeToX(t: number): number {
    return HEADER_W + t * this.pxPerSecond;
  }
  xToTime(x: number): number {
    return Math.max(0, (x - HEADER_W) / this.pxPerSecond);
  }
  trackIndexAtY(y: number): number {
    if (y < RULER_H) return -1;
    return Math.floor((y - RULER_H) / TRACK_H);
  }
  clipRect(trackIndex: number, startSec: number, durationSec: number) {
    return {
      x: this.timeToX(startSec),
      y: RULER_H + trackIndex * TRACK_H + CLIP_PAD,
      w: Math.max(2, durationSec * this.pxPerSecond),
      h: TRACK_H - CLIP_PAD * 2,
    };
  }

  hitTest(x: number, y: number): HitResult {
    const tracks = this.ctx.store.state.project.tracks;
    if (y < RULER_H) return { kind: 'ruler' };

    const trackIndex = this.trackIndexAtY(y);
    if (trackIndex < 0 || trackIndex >= tracks.length) return { kind: 'outside' };

    // Track header zone (left gutter): the ✕ remove target sits at its right edge.
    if (x < HEADER_W) {
      if (x > HEADER_W - 26 && x < HEADER_W - 6 && y < RULER_H + trackIndex * TRACK_H + 26) {
        return { kind: 'remove-track', trackIndex };
      }
      return { kind: 'empty', trackIndex };
    }

    const track = tracks[trackIndex];
    for (const clip of track.clips) {
      const r = this.clipRect(trackIndex, clip.startSec, clip.durationSec);
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
        // For clips narrower than 2×EDGE_PX, shrink the edge zone to half-width so
        // the start/end zones split the clip evenly instead of the start zone
        // swallowing the whole clip (which made the end edge un-trimmable).
        const edge = Math.min(EDGE_PX, r.w / 2);
        if (x <= r.x + edge) return { kind: 'clip', clipId: clip.id, trackIndex, edge: 'start' };
        if (x >= r.x + r.w - edge) return { kind: 'clip', clipId: clip.id, trackIndex, edge: 'end' };
        return { kind: 'clip', clipId: clip.id, trackIndex, edge: 'body' };
      }
    }
    return { kind: 'empty', trackIndex };
  }

  // ---- drawing ---------------------------------------------------------------
  draw(): void {
    const state = this.ctx.store.state;
    const tracks = state.project.tracks;
    const duration = Math.max(projectDuration(state.project), 4);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const wrap = this.canvas.parentElement;
    const minWidth = wrap ? wrap.clientWidth : 800;
    const cssWidth = Math.max(minWidth, this.timeToX(duration) + 80);
    const cssHeight = RULER_H + tracks.length * TRACK_H;

    if (this.canvas.width !== Math.round(cssWidth * dpr) || this.canvas.height !== Math.round(cssHeight * dpr)) {
      this.canvas.width = Math.round(cssWidth * dpr);
      this.canvas.height = Math.round(cssHeight * dpr);
      this.canvas.style.width = cssWidth + 'px';
      this.canvas.style.height = cssHeight + 'px';
    }
    const g = this.g;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, cssWidth, cssHeight);

    // Lane backgrounds + track headers.
    for (let i = 0; i < tracks.length; i++) {
      const yTop = RULER_H + i * TRACK_H;
      g.fillStyle = i % 2 === 0 ? '#161616' : '#121212';
      g.fillRect(HEADER_W, yTop, cssWidth - HEADER_W, TRACK_H);

      g.fillStyle = '#1c1a17';
      g.fillRect(0, yTop, HEADER_W, TRACK_H);
      g.fillStyle = '#a39c8d';
      g.font = '12px system-ui, sans-serif';
      g.textAlign = 'left';
      g.textBaseline = 'middle';
      g.fillText(tracks[i].name, 12, yTop + TRACK_H / 2, HEADER_W - 36);
      // remove ✕
      g.fillStyle = '#6b6357';
      g.fillText('✕', HEADER_W - 22, yTop + 13);
    }

    // Ruler.
    g.fillStyle = '#1c1a17';
    g.fillRect(0, 0, cssWidth, RULER_H);
    this.drawRuler(g, cssWidth);

    // Clips.
    for (let i = 0; i < tracks.length; i++) {
      for (const clip of tracks[i].clips) {
        this.drawClip(g, i, clip, state.selectedClipId === clip.id);
      }
    }

    // Header/lane separators.
    g.strokeStyle = '#3a352d';
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(HEADER_W + 0.5, 0);
    g.lineTo(HEADER_W + 0.5, cssHeight);
    g.stroke();

    // Snap guideline (white dashed) — shown while a drag is snapped to a target.
    if (this.snapLine != null) {
      const sx = this.timeToX(this.snapLine);
      g.save();
      g.strokeStyle = '#ffffff';
      g.setLineDash([4, 4]);
      g.beginPath();
      g.moveTo(sx + 0.5, 0);
      g.lineTo(sx + 0.5, cssHeight);
      g.stroke();
      g.restore();
    }

    // Playhead.
    const px = this.timeToX(this.ctx.player.getTime());
    g.strokeStyle = '#ffd21e';
    g.beginPath();
    g.moveTo(px + 0.5, 0);
    g.lineTo(px + 0.5, cssHeight);
    g.stroke();
    g.fillStyle = '#ffd21e';
    g.beginPath();
    g.moveTo(px - 5, 0);
    g.lineTo(px + 5, 0);
    g.lineTo(px, 8);
    g.closePath();
    g.fill();
  }

  private drawRuler(g: CanvasRenderingContext2D, cssWidth: number): void {
    const pps = this.pxPerSecond;
    // Choose a "nice" tick interval so labels don't crowd.
    const targetPx = 90;
    const rawStep = targetPx / pps;
    const step = niceStep(rawStep);
    g.fillStyle = '#8a8275';
    g.strokeStyle = '#3a352d';
    g.font = '10px system-ui, sans-serif';
    g.textAlign = 'left';
    g.textBaseline = 'top';
    for (let t = 0; this.timeToX(t) < cssWidth; t += step) {
      const x = this.timeToX(t);
      g.beginPath();
      g.moveTo(x + 0.5, RULER_H - 6);
      g.lineTo(x + 0.5, RULER_H);
      g.stroke();
      g.fillText(formatTimecode(t), x + 3, 5);
    }
  }

  private drawClip(g: CanvasRenderingContext2D, trackIndex: number, clip: Clip, selected: boolean): void {
    // While dragging, render the transient position/size instead.
    let startSec = clip.startSec;
    let durationSec = clip.durationSec;
    let drawTrack = trackIndex;
    if (this.drag && this.drag.clipId === clip.id) {
      startSec = this.drag.startSec;
      durationSec = this.drag.durationSec;
      drawTrack = this.drag.trackIndex;
    }
    const r = this.clipRect(drawTrack, startSec, durationSec);

    // text = light/white block, image = yellow, audio = green, video = orange.
    const fill =
      clip.kind === 'text'
        ? '#ece2cf'
        : clip.kind === 'image'
          ? '#ffce3a'
          : clip.kind === 'audio'
            ? '#46d39a'
            : '#ff8a00';
    roundRect(g, r.x, r.y, r.w, r.h, 6);
    g.fillStyle = fill;
    g.fill();
    if (selected) {
      g.strokeStyle = '#ffffff';
      g.lineWidth = 2;
      g.stroke();
    }

    // Label.
    g.save();
    roundRect(g, r.x, r.y, r.w, r.h, 6);
    g.clip();
    g.fillStyle = 'rgba(0,0,0,0.78)';
    g.font = '11px system-ui, sans-serif';
    g.textAlign = 'left';
    g.textBaseline = 'middle';
    const label = clip.kind === 'text' ? `T  ${(clip as { text: string }).text || 'Text'}` : clip.name;
    g.fillText(label, r.x + 8, r.y + r.h / 2, r.w - 12);
    g.restore();
  }
}

// Snap a raw step (seconds) up to a friendly 1/2/5 × 10ⁿ value.
function niceStep(raw: number): number {
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1e-6))));
  const base = raw / pow;
  const mult = base <= 1 ? 1 : base <= 2 ? 2 : base <= 5 ? 5 : 10;
  return Math.max(0.1, mult * pow);
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + radius, y);
  g.arcTo(x + w, y, x + w, y + h, radius);
  g.arcTo(x + w, y + h, x, y + h, radius);
  g.arcTo(x, y + h, x, y, radius);
  g.arcTo(x, y, x + w, y, radius);
  g.closePath();
}
